/**
 * 将站内发品表单组装为 TikTok Shop OpenAPI Create Product 请求体
 * （POST /product/202309/products）。
 *
 * 必填：title、description、category_id、main_images、package_weight、skus
 * 可选：brand_id、package_dimensions、product_attributes、video、size_chart 等
 */

import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { findCategoryPathIds } from '@/tiktok-platform/utils/tiktokCategoryTree';
import {
  isBlankTiktokAttributeValueId,
  resolveSkcColorAgainstTemplate,
} from '@/tiktok-platform/utils/tiktokAttributeTemplate';
import { toTiktokSubmitColorName } from '@/tiktok-platform/utils/tiktokColorNameMap';
import { isEditorAttrVisible } from '@/tiktok-platform/utils/tiktokAttrVisibility';
import {
  buildTiktokCertificationsPayload,
  buildTiktokIsCodAllowed,
  buildTiktokSkuFees,
  normalizeTiktokComplianceIds,
  toUnixSeconds,
} from '@/tiktok-platform/utils/tiktokCategoryRules';
import {
  clampTiktokStringList,
  isTiktokEuMarketRegion,
  parseTiktokStringList,
  resolveTiktokPreSaleRule,
  resolveTiktokScheduledSaleMillis,
  TIKTOK_IDEMPOTENCY_KEY_MAX,
  TIKTOK_INVENTORY_QTY_MAX,
  TIKTOK_INVENTORY_QTY_MIN,
  TIKTOK_KEY_FEATURES_MAX,
  TIKTOK_KEY_FEATURES_MAX_CHARS,
  TIKTOK_SEARCH_TERMS_MAX,
  TIKTOK_SEARCH_TERMS_MAX_CHARS,
} from '@/tiktok-platform/utils/tiktokPublishFormLimits';

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function asText(v) {
  return String(v ?? '').trim();
}

/**
 * Create Product / listing_check 的图片字段只能用 Upload Image 返回的 material uri
 * （形如 tos-xxx/...），禁止把 CDN https 地址当 uri（平台 code=12052722）。
 */
export function isTiktokMaterialUri(value) {
  const s = asText(value);
  if (!s) return '';
  if (/^https?:\/\//i.test(s) || s.startsWith('//') || s.startsWith('blob:') || s.startsWith('data:')) {
    return '';
  }
  // 站内 /uploads 路径也不是平台 uri
  if (s.startsWith('/')) return '';
  return s;
}

/** Create Product 图片字段：只取 Upload Image 的 uri */
export function imageUriForTts(image) {
  if (!image) return '';
  if (typeof image === 'string') return isTiktokMaterialUri(image);
  return isTiktokMaterialUri(image.uri)
    || isTiktokMaterialUri(image.tiktokUri)
    || '';
}

function moneyAmount(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return '';
  return n.toFixed(2);
}

/** 归一化市场码（UK → GB）。 */
function normalizeMarketRegion(raw) {
  const code = asText(raw).toUpperCase();
  return code === 'UK' ? 'GB' : code;
}

/**
 * 商品条码格式（Create Product `skus[].identifier_code` 官方规则）。
 * 不合法的值直接丢弃，不把脏条码发给平台（平台会整单拒）。
 */
function isIdentifierCodeShaped(type, value) {
  switch (type) {
    case 'GTIN':
      return /^\d{14}$/.test(value);
    case 'EAN':
      return /^\d{8}$/.test(value) || /^\d{13}$/.test(value) || /^\d{14}$/.test(value);
    case 'UPC':
      return /^\d{12}$/.test(value);
    case 'ISBN':
      return /^\d{13}$/.test(value) || /^\d{8}X$/.test(value);
    case 'JAN':
      return /^\d{8}$/.test(value) || /^\d{13}$/.test(value);
    default:
      return false;
  }
}

/** 按条码长度/形态推断类型（用户选错类型时的兜底，避免有效条码被丢弃）。 */
function inferIdentifierCodeType(value) {
  if (/^\d{14}$/.test(value)) return 'GTIN';
  if (/^\d{12}$/.test(value)) return 'UPC';
  if (/^\d{13}$/.test(value)) return 'EAN';
  if (/^\d{8}X$/.test(value)) return 'ISBN';
  if (/^\d{8}$/.test(value)) return 'EAN';
  return '';
}

/** @returns {{ code: string, type: string } | null} */
export function normalizeTiktokIdentifierCode(code, type) {
  const value = asText(code).replace(/[\s-]/g, '').toUpperCase();
  if (!value) return null;
  const kind = asText(type).toUpperCase();
  if (isIdentifierCodeShaped(kind, value)) return { code: value, type: kind };
  // 显式类型与条码形态不符时，按条码本身推断；仍无法判定则整条丢弃
  const inferred = inferIdentifierCodeType(value);
  return inferred ? { code: value, type: inferred } : null;
}

/** 商品条码可选项（下拉用）。 */
export const TIKTOK_IDENTIFIER_CODE_TYPES = ['GTIN', 'EAN', 'UPC', 'ISBN', 'JAN'];

/** 支持自定义配送方式（delivery_option_ids）的市场。 */
export const TIKTOK_DELIVERY_OPTION_MARKETS = new Set(['ID', 'MX', 'MY', 'PH', 'SG', 'TH', 'VN']);

function wrapDescriptionHtml(raw) {
  const text = asText(raw);
  if (!text) return '';
  if (/<\s*(p|div|ul|ol|br|img|span)\b/i.test(text)) {
    return text.slice(0, 10000);
  }
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n+/g, '</p><p>');
  return `<p>${escaped}</p>`.slice(0, 10000);
}

function resolveCatIds(form) {
  if (Array.isArray(form.catPathIds) && form.catPathIds.length) {
    return form.catPathIds.map((id) => asText(id)).filter(Boolean);
  }
  if (Array.isArray(form.categoryPathIds) && form.categoryPathIds.length) {
    return form.categoryPathIds.map((id) => asText(id)).filter(Boolean);
  }
  const fromTree = findCategoryPathIds(
    form.categoryTree || form.categories || [],
    form.categoryId || form.productTypeId,
  );
  return (fromTree || []).map((id) => asText(id)).filter(Boolean);
}

function collectMainImageUris(form) {
  const uris = [];
  const push = (img) => {
    // Create Product main_images 只能传 Upload Image 的 uri，禁止 CDN url（12052722）
    const uri = imageUriForTts(img);
    if (uri && !uris.includes(uri)) uris.push(uri);
  };
  // 商品级主图 / 细节图优先（不按颜色分）
  push(form?.mainImage);
  (Array.isArray(form?.detailImages) ? form.detailImages : []).forEach(push);
  // 旧草稿回退：色块级 main/detail；展厅位仅兼容历史数据
  if (!uris.length) {
    (form.skcList || []).forEach((skc) => {
      push(skc?.mainImage);
      (skc?.detailImages || []).forEach(push);
      Object.values(skc?.showroomSlots || {}).forEach(push);
    });
  }
  (form.carouselImages || []).forEach(push);
  // 不把预览 CDN url / 素材图 / 色块图塞进 main_images
  return uris.slice(0, 9);
}

function collectTiktokPropDefs(attrTpl) {
  const map = new Map();
  const rows = [
    ...(attrTpl?.productAttrs || []),
    ...(attrTpl?.requiredProduct || []),
    ...(attrTpl?.optionalProduct || []),
    ...(attrTpl?.skcSaleAttrs || []),
    ...(attrTpl?.skuSaleAttrs || []),
  ];
  rows.forEach((row) => {
    const id = asText(row?.attributeId || row?.templatePid || row?.pid);
    if (id) map.set(id, row);
  });
  return map;
}

function findProductAttrValueDef(def, token) {
  const key = asText(token);
  if (!key || !def) return null;
  const rows = Array.isArray(def.values) ? def.values : [];
  const byId = rows.find((opt) => asText(opt?.id || opt?.valueId || opt?.attribute_value_id) === key);
  if (byId) return byId;
  return rows.find((opt) => {
    const n = asText(opt?.name || opt?.nameCn || opt?.value);
    return n && n === key;
  }) || null;
}

/**
 * product_attributes.values：
 * - 平台枚举：只传 id（文档：id / name 二选一；带错 name 会 12052247/11001002）
 * - 成分属性：id + name=比例
 * - 手输/自定义：只传 name
 * AttributeField 下拉存的是 valueId 字符串，禁止写成 { name: "1000850" }。
 */
function mapProductAttrValue(def, v) {
  if (v == null || v === '') return null;
  if (typeof v === 'object') {
    const id = asText(v.id || v.valueId || v.attribute_value_id || v.attributeValueId);
    const name = asText(v.name || v.value || v.attribute_value);
    const percent = asText(v.percent ?? v.attributeExtraValue);
    if (id) {
      // 成分属性：比例放在 name
      if (percent) return { id, name: percent };
      return { id };
    }
    if (name) return { name };
    return null;
  }
  const t = asText(v);
  if (!t) return null;
  const matched = findProductAttrValueDef(def, t);
  if (matched) {
    return { id: asText(matched.id || matched.valueId || t) };
  }
  // 下拉值为纯数字 id，但模板 values 未加载：仍按 id 提交，禁止当自定义名
  if (/^\d+$/.test(t)) {
    return { id: t };
  }
  // 手输（mode=0）或明确可自定义
  if (def?.mode === 0 || def?.isCustomizable === true) {
    return { name: t };
  }
  // 有平台枚举却未匹配：丢弃，避免把非法自定义名发给平台
  if (Array.isArray(def?.values) && def.values.length) {
    return null;
  }
  return { name: t };
}

function buildProductAttributes(form, attrTpl) {
  const attrs = form?.productAttributes && typeof form.productAttributes === 'object'
    ? form.productAttributes
    : {};
  const out = [];
  const defs = collectTiktokPropDefs(attrTpl);
  Object.entries(attrs).forEach(([attrId, raw]) => {
    const def = defs.get(String(attrId));
    if (def && !isEditorAttrVisible(def, attrs)) return;
    const values = [];
    const pushVal = (v) => {
      const mapped = mapProductAttrValue(def, v);
      if (mapped) values.push(mapped);
    };
    if (Array.isArray(raw)) raw.forEach(pushVal);
    else pushVal(raw);
    if (!values.length) return;
    out.push({
      id: asText(attrId),
      values,
    });
  });
  return out;
}

function firstPositiveNumber(...values) {
  for (const raw of values) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

/** 单个 SKU 的包裹参数：SKU 自身值优先，缺省回落到商品级 form 默认值。 */
function skuPackageOf(sku = {}, form = {}) {
  return {
    weightG: firstPositiveNumber(sku.weight, sku.weightGrams, sku.inputWeight, form.weightGrams),
    lengthCm: firstPositiveNumber(sku.length, form.pkgLength),
    widthCm: firstPositiveNumber(sku.width, form.pkgWidth),
    heightCm: firstPositiveNumber(sku.height, form.pkgHeight),
  };
}

/**
 * 商品级包裹参数（Create Product：一商品填一次）。
 * 优先 form.weightGrams / pkgLength|Width|Height；旧草稿只写在 SKU 行时再回退取大。
 */
function productLevelPackage(form) {
  const fromForm = {
    weightG: firstPositiveNumber(form.weightGrams),
    lengthCm: firstPositiveNumber(form.pkgLength),
    widthCm: firstPositiveNumber(form.pkgWidth),
    heightCm: firstPositiveNumber(form.pkgHeight),
  };
  if (fromForm.weightG > 0 && fromForm.lengthCm > 0 && fromForm.widthCm > 0 && fromForm.heightCm > 0) {
    return fromForm;
  }
  const rows = [];
  (form.skcList || []).forEach((skc) => {
    const skuRows = Array.isArray(skc?.skuRows) && skc.skuRows.length ? skc.skuRows : [{}];
    skuRows.forEach((sku) => rows.push(skuPackageOf(sku, form)));
  });
  const maxOf = (key) => rows.reduce((acc, row) => Math.max(acc, row[key] || 0), 0);
  return {
    weightG: fromForm.weightG || maxOf('weightG'),
    lengthCm: fromForm.lengthCm || maxOf('lengthCm'),
    widthCm: fromForm.widthCm || maxOf('widthCm'),
    heightCm: fromForm.heightCm || maxOf('heightCm'),
  };
}

function buildSalesAttributes({
  colorAttr,
  sizeAttr,
  resolvedColor,
  sku,
  defs,
  colorMainImage = null,
  fallbackSkuImage = null,
}) {
  // sku_img：供应信息按颜色维护的变体图；旧草稿无变体图时回退色块主图。
  const variantImage = fallbackSkuImage || sku?.skuImage || colorMainImage;
  const variantImageUri = imageUriForTts(variantImage);
  const sales = [];
  if (colorAttr && resolvedColor) {
    const def = defs.get(String(colorAttr.attributeId));
    const valueId = asText(resolvedColor.colorValueId || '');
    const platformValueId = isBlankTiktokAttributeValueId(valueId) ? '' : valueId;
    const matched = platformValueId
      ? (def?.rawValues || def?.values || []).find(
        (v) => String(v.attribute_value_id || v.id) === String(platformValueId),
      )
      : null;
    const attrId = asText(colorAttr.attributeId || def?.tiktokProp?.parentSpecId || '');
    if (platformValueId || matched) {
      // 平台内置销售属性：只传 id + value_id（+ 平台原样 value_name）；
      // 勿带 name——name 仅用于「自定义销售属性名」，与内置 id 同传易触发网关校验失败
      const valueName = asText(matched?.attribute_value || matched?.name || resolvedColor.colorLabel || '');
      if (attrId && (platformValueId || valueName)) {
        const row = { id: attrId };
        if (platformValueId) row.value_id = platformValueId;
        if (valueName) row.value_name = valueName;
        if (variantImageUri) row.sku_img = { uri: variantImageUri };
        sales.push(row);
      }
    } else {
      // 自定义颜色值：仍用平台属性 id，只给 value_name，禁止 value_id=0；
      // 站内默认中文色名，提交前统一转英文；未映射且含中文则拒绝（平台不接受）
      const customName = asText(resolvedColor.colorExtraValue || resolvedColor.colorLabel);
      const valueName = toTiktokSubmitColorName(customName);
      if (customName && !valueName) {
        throw new Error(fixedUiTpl(
          '自定义颜色「{0}」须为英文（平台不接受中文）。常见色名会自动转英文，其余请改用英文填写',
          customName,
        ));
      }
      if (attrId && valueName) {
        const row = { id: attrId, value_name: valueName };
        if (variantImageUri) row.sku_img = { uri: variantImageUri };
        sales.push(row);
      }
    }
  }
  if (sizeAttr) {
    const def = defs.get(String(sizeAttr.attributeId));
    const attrId = asText(sizeAttr.attributeId || def?.tiktokProp?.parentSpecId || '');
    const valueId = asText(sku?.sizeValueId || '');
    const matched = isBlankTiktokAttributeValueId(valueId)
      ? null
      : (def?.rawValues || def?.values || []).find(
        (v) => String(v.attribute_value_id || v.id) === String(valueId),
      );
    // 自定义尺码（平台无内置值 / 手输）：只给 value_name；内置尺码给 value_id
    const customName = asText(
      sku?.sizeExtraValue
      || (isBlankTiktokAttributeValueId(valueId) ? (sku?.sizeLabel || '') : ''),
    );
    const valueName = asText(customName || matched?.attribute_value || sku?.sizeLabel || '');
    if (attrId && (!isBlankTiktokAttributeValueId(valueId) || valueName)) {
      const row = { id: attrId };
      if (!isBlankTiktokAttributeValueId(valueId)) row.value_id = valueId;
      if (valueName) row.value_name = valueName;
      sales.push(row);
    }
  }
  return sales.filter((row) => row.id);
}

/**
 * `skus[].pre_sale`：仅当 Get Category Rules 的 `allowed_special_product_types`
 * 非空且**包含所选 type** 时装配（缺失 = 无权限，不装配）。
 * 所有 SKU 必须同 type（平台 12019100），故由表单级配置统一生成。
 * @returns {null | { type: string, fulfillment_type: object }}
 */
function resolveTiktokPreSalePayload(form, { region, categoryRules }) {
  const type = asText(form?.preSaleType).toUpperCase();
  if (!type) return null;
  const allowed = (Array.isArray(categoryRules?.allowedSpecialProductTypes)
    ? categoryRules.allowedSpecialProductTypes
    : []).map((row) => asText(row).toUpperCase());
  if (!allowed.includes(type)) return null;
  const rule = resolveTiktokPreSaleRule(region, type);
  if (!rule) return null;
  if (rule.fulfillment === 'release_date') {
    // 仅 US PRE_ORDER：Unix 秒
    const releaseDate = toUnixSeconds(form?.preSaleReleaseDate);
    if (!(releaseDate > 0)) return null;
    return { type, fulfillment_type: { release_date: releaseDate } };
  }
  const days = toInt(form?.preSaleHandlingDays, 0);
  if (!(days > 0)) return null;
  return { type, fulfillment_type: { handling_duration_days: days } };
}

/**
 * `purchase_order_quantity_limit`：两字段任一为有效正整数才装配；
 * 两者皆填时按文档「累计 ≥ 单次」取有效组合（不满足则只发单次，由校验器报错）。
 */
function resolveTiktokPurchaseOrderLimit(form) {
  const maximum = toPositiveInt(form?.maxOrderQuantity);
  const cumulative = toPositiveInt(form?.cumulativeOrderQuantity);
  const out = {};
  if (maximum > 0) out.maximum_order_quantity = maximum;
  if (cumulative > 0 && (maximum <= 0 || cumulative >= maximum)) {
    out.cumulative_order_quantity = cumulative;
  }
  return Object.keys(out).length ? out : null;
}

function toPositiveInt(raw) {
  const text = asText(raw);
  if (!/^\d+$/.test(text)) return 0;
  const n = Number(text);
  return n > 0 ? Math.trunc(n) : 0;
}

/**
 * @param {object} form Editor 表单
 * @param {object} options
 * @param {boolean} [options.globalSeller] 跨境店=全球卖家；仅 US/JP 市场补 sale_price
 * @param {string} [options.categoryVersion] Create Product 类目树版本（US/SEA 必传 v2）
 * @param {object} [options.categoryRules] Get Category Rules 归一化结果（认证/COD/PFAND/EU 合规实体）
 * @returns {object} TTS Create Product body
 */
export function buildTiktokTtsCreateProductPayload(form = {}, {
  attrTpl = null,
  categories = null,
  colorAttr = null,
  sizeAttr = null,
  globalSeller = false,
  categoryVersion = '',
  categoryRules = null,
  region = '',
  titleMaxLength = 0,
  saveMode = '',
  /** 幂等键：由提交路径在核验通过后生成并传入（每次提交一个） */
  idempotencyKey = '',
} = {}) {
  const catIds = resolveCatIds({
    ...form,
    categoryTree: categories || form.categoryTree,
    categories: categories || form.categories,
  });
  const categoryId = asText(form.categoryId || form.productTypeId || catIds[catIds.length - 1]);
  if (!categoryId) {
    throw new Error(fixedUi('请先选择叶子类目'));
  }

  const title = asText(form.title || form.productName);
  if (!title) {
    throw new Error(fixedUi('请填写商品名称'));
  }

  let description = wrapDescriptionHtml(form.description);
  if (!description) {
    // 平台要求 description；未填时用英文名/标题生成最小合规 HTML
    const fallback = asText(form.titleEn || form.englishTitle) || title;
    description = wrapDescriptionHtml(fallback);
  }

  const mainUris = collectMainImageUris(form);
  if (!mainUris.length) {
    throw new Error(fixedUi('请至少上传 1 张商品主图（须完成 TikTok 图片上传）'));
  }

  const { weightG, lengthCm, widthCm, heightCm } = productLevelPackage(form);
  if (!(weightG > 0)) {
    throw new Error(fixedUi('请填写包装重量（克）'));
  }

  const currency = asText(form.currency || 'USD').toUpperCase() || 'USD';
  const marketRegion = normalizeMarketRegion(region || form.region || form.tiktokCountry);
  const isUsMarket = marketRegion === 'US';
  const resolvedMaxTitleLength = Number(titleMaxLength) > 0 ? Number(titleMaxLength) : 255;
  const warehouseId = asText(form.warehouseId);
  const defs = collectTiktokPropDefs(attrTpl);
  const color = colorAttr || attrTpl?.skcSaleAttrs?.[0] || null;
  const size = sizeAttr || attrTpl?.skuSaleAttrs?.[0] || null;

  const skus = [];
  // TK-PUB-23：非法/缺失价格此前会静默 delete price，导致 SKU 无价、整单被平台拒
  const invalidPriceRows = [];
  const invalidStockRows = [];
  // 类目要求 PFAND 押金时，押金挂在每个 SKU 上（Amount 为当地币种）
  const skuFees = buildTiktokSkuFees(form, categoryRules);
  // 预售：文档要求同一商品所有 SKU 同 type，故表单级解析一次后逐 SKU 复制
  const preSale = resolveTiktokPreSalePayload(form, { region: marketRegion, categoryRules });
  const skcSource = Array.isArray(form.skcList) && form.skcList.length ? form.skcList : [{}];
  skcSource.forEach((skc, skcIndex) => {
    // 与 validateTiktokPublishForm 同口径：签名只吃 (skc, colorAttr)，不接受 attrTpl
    const resolved = resolveSkcColorAgainstTemplate(skc, color) || {};
    const skuRows = Array.isArray(skc.skuRows) && skc.skuRows.length
      ? skc.skuRows
      : [{ supplierSku: '' }];
    // 商品级货号优先（基础信息）；色块级仅兼容旧草稿
    const skcExt = asText(form.supplierCode || resolved.supplierCode || skc.supplierCode);

    skuRows.forEach((sku, skuIndex) => {
      const priceRaw = sku.supplyPrice ?? sku.price ?? form.supplyPrice ?? form.defaultPriceYuan;
      const amount = moneyAmount(priceRaw);
      const stock = toInt(sku.stock ?? form.stockQty ?? form.defaultStock, 0);
      // seller_sku：官方要求无空格；空格会导致 11001002
      const sellerSku = asText(
        sku.supplierSku
        || (skcExt ? `${skcExt}-${skuIndex + 1}` : `SKU-${skcIndex + 1}-${skuIndex + 1}`),
      ).replace(/\s+/g, '');
      const colorLabel = asText(resolved.colorLabel || resolved.colorName || skc.colorLabel);
      const rowLabel = `${colorLabel || `SKC${skcIndex + 1}`} / ${sellerSku}`;
      if (!amount) {
        invalidPriceRows.push(rowLabel);
      }
      // Create Product：quantity Valid range [1, 99999]；传 0 会 11001002
      if (stock < TIKTOK_INVENTORY_QTY_MIN || stock > TIKTOK_INVENTORY_QTY_MAX) {
        invalidStockRows.push(rowLabel);
      }
      const sales_attributes = buildSalesAttributes({
        colorAttr: color,
        sizeAttr: size,
        resolvedColor: resolved,
        sku,
        defs,
        colorMainImage: skc.mainImage,
        fallbackSkuImage: skc.skuImage || null,
      });

      const inv = { quantity: stock };
      if (warehouseId) inv.warehouse_id = warehouseId;

      // 价格字段口径（Create Product 202309）：
      // - amount：本土 / Intra-EU 展示价
      // - sale_price：全球卖家（跨境）展示价；官方注明 JP/US 中国仓必填，其它市场可选
      // 客服排查 11001002：非必填市场勿带 sale_price（与 amount 同传易被网关判非法）。
      // 组套字段 primary_combined_product_id / combined_skus 仅 Virtual Bundle 使用，普通发品绝不下发。
      const needSalePrice = Boolean(globalSeller)
        && (marketRegion === 'US' || marketRegion === 'JP');
      const price = needSalePrice
        ? { amount, currency, sale_price: amount }
        : { amount, currency };

      const mapped = {
        seller_sku: sellerSku,
        price,
        inventory: [inv],
      };
      const platformSkuId = asText(sku.platformSkuId || sku.skuId || sku.tiktokSkuId);
      if (platformSkuId) mapped.id = platformSkuId;
      if (sales_attributes.length) mapped.sales_attributes = sales_attributes;
      if (skuFees) mapped.fees = skuFees;
      if (preSale) {
        mapped.pre_sale = {
          type: preSale.type,
          fulfillment_type: { ...preSale.fulfillment_type },
        };
      }
      // 商品条码（GTIN/EAN/UPC/ISBN/JAN）：格式不合法直接不发，避免平台整单拒
      const identifierCode = normalizeTiktokIdentifierCode(
        sku.identifierCode ?? sku.identifier_code,
        sku.identifierType ?? sku.identifier_code_type,
      );
      if (identifierCode) mapped.identifier_code = identifierCode;
      // US 划线价（list_price = MSRP/RRP）：官方仅 US 市场生效，币种固定 USD，
      // 且须 >= 实际售价、<= 7600，否则平台直接丢弃
      if (isUsMarket) {
        const listAmount = moneyAmount(sku.suggestedPrice || form.suggestedPrice);
        const listNum = Number(listAmount);
        const priceNum = Number(amount || 0);
        if (listAmount && listNum > 0 && listNum >= priceNum && listNum <= 7600) {
          mapped.list_price = { amount: listAmount, currency: 'USD' };
        }
      }
      // Create Product：包裹用顶层 package_weight / package_dimensions（一商品一次），不逐 SKU 覆盖。
      skus.push(mapped);
    });
  });

  if (invalidPriceRows.length) {
    throw new Error(fixedUiTpl(
      '以下 SKU 缺少有效售价（须为 > 0 的数字）：{0}{1}',
      invalidPriceRows.slice(0, 6).join(fixedUi('、')),
      invalidPriceRows.length > 6 ? fixedUi('…') : '',
    ));
  }
  if (invalidStockRows.length) {
    throw new Error(fixedUiTpl(
      '以下 SKU 库存须在 {0}–{1}：{2}{3}',
      TIKTOK_INVENTORY_QTY_MIN,
      TIKTOK_INVENTORY_QTY_MAX,
      invalidStockRows.slice(0, 6).join(fixedUi('、')),
      invalidStockRows.length > 6 ? fixedUi('…') : '',
    ));
  }

  if (!skus.length) {
    throw new Error(fixedUi('请至少配置 1 个 SKU'));
  }

  const out = {
    title: title.slice(0, resolvedMaxTitleLength),
    description,
    category_id: categoryId,
    main_images: mainUris.map((uri) => ({ uri })),
    package_weight: {
      // KILOGRAM 最多 3 位小数；避免 0.20000000004 一类浮点脏串
      value: String(Math.max(0.001, Number((weightG / 1000).toFixed(3)))),
      unit: 'KILOGRAM',
    },
    skus,
  };
  // Create Product 顶层 category_version：US/SEA 必须 v2，否则 leaf category 归属错版
  const catVersion = asText(categoryVersion || form.categoryVersion);
  if (catVersion) out.category_version = catVersion;

  // save_mode：AS_DRAFT = 只存平台草稿，不进入审核/上架（默认 LISTING）
  if (asText(saveMode).toUpperCase() === 'AS_DRAFT') out.save_mode = 'AS_DRAFT';

  if (lengthCm > 0 && widthCm > 0 && heightCm > 0) {
    // 官方：长宽高须为正整数（BR 除外）；避免浮点字符串触发 11001002
    out.package_dimensions = {
      length: String(Math.max(1, Math.round(lengthCm))),
      width: String(Math.max(1, Math.round(widthCm))),
      height: String(Math.max(1, Math.round(heightCm))),
      unit: 'CENTIMETER',
    };
  }

  const brandId = asText(form.brandCode || form.brandId || form.brand_id);
  if (brandId) out.brand_id = brandId;

  const shippingTemplateId = asText(
    form.shippingTemplateId || form.freightTemplateId || form.tiktokFreightTemplateId,
  );
  if (shippingTemplateId) out.shipping_template_id = shippingTemplateId;

  const productAttributes = buildProductAttributes(form, attrTpl);
  if (productAttributes.length) out.product_attributes = productAttributes;

  // 尺码表：template.id 只能是**平台**模板 id（来自尺码表面板 resolveForSubmit，见 TK-PUB-11）。
  // 站内 form.sizeChartTemplateId 是本地区块 id，发给平台会被判为非法模板（TK-PUB-21），故不再回退。
  const sizeChart = {};
  const sizeChartImageUri = imageUriForTts(form.sizeChartImage || form.tiktokSizeChartImage);
  if (sizeChartImageUri) {
    sizeChart.image = { uri: sizeChartImageUri };
  }
  const platformSizeTplId = (Array.isArray(form.tiktokSizeTemplateIds)
    ? form.tiktokSizeTemplateIds
    : [])
    .map((id) => asText(id))
    .filter((id) => id && id !== '0')
    .slice(0, 1)[0] || '';
  if (platformSizeTplId) {
    sizeChart.template = { id: platformSizeTplId };
  }
  if (sizeChart.image || sizeChart.template) {
    out.size_chart = sizeChart;
  }

  const video = Array.isArray(form.tiktokVideos) ? form.tiktokVideos.find((v) => v && (v.vid || v.videoUrl)) : null;
  if (video) {
    // Create Product 的 video 只接受 { id }（Upload Product File 返回的 file id）。
    // 传 url 属未知字段，会被平台判为非法参数，故仅在拿到 vid 时装配。
    const vid = asText(video.vid);
    if (vid) {
      out.video = { id: vid };
    }
  }

  const externalId = asText(form.supplierCode || form.externalProductId);
  if (externalId) out.external_product_id = externalId;

  // —— Get Category Rules 驱动项（TK-PUB-25）——
  // COD：类目不支持时不得传 true，否则 listing 失败
  const isCodAllowed = buildTiktokIsCodAllowed(form, categoryRules);
  if (isCodAllowed !== undefined) out.is_cod_allowed = isCodAllowed;

  // 受限类目认证：图片走 Upload Image（use_case=CERTIFICATION_IMAGE），PDF 走 Upload Product File
  const certifications = buildTiktokCertificationsPayload(form, categoryRules?.certifications);
  if (certifications.length) out.certifications = certifications;

  // EU/UK 合规实体：仅欧盟与英国市场可传，且为选填（其他市场不提交，避免脏数据）
  if (isTiktokEuMarketRegion(marketRegion)) {
    const manufacturerIds = normalizeTiktokComplianceIds(form.manufacturerIds);
    if (manufacturerIds.length) out.manufacturer_ids = manufacturerIds;
    const responsiblePersonIds = normalizeTiktokComplianceIds(form.responsiblePersonIds);
    if (responsiblePersonIds.length) out.responsible_person_ids = responsiblePersonIds;
  }

  // 自定义配送方式：仅 ID/MX/MY/PH/SG/TH/VN 支持 delivery_option_ids；
  // 其他市场（US/GB/EU/JP/BR）传了不会被处理，故按市场白名单过滤。
  // 另：官方注明 save_mode=AS_DRAFT 时 delivery_option_ids 不落库，故草稿态不发（TK-PUB-31）
  if (TIKTOK_DELIVERY_OPTION_MARKETS.has(marketRegion) && asText(saveMode).toUpperCase() !== 'AS_DRAFT') {
    const deliveryOptionIds = (Array.isArray(form.deliveryOptionIds)
      ? form.deliveryOptionIds
      : String(form.deliveryOptionIds || '').split(','))
      .map((id) => asText(id))
      .filter(Boolean);
    if (deliveryOptionIds.length) out.delivery_option_ids = deliveryOptionIds;
  }

  // —— 预约上架（scheduled_sale）——
  // 非空即视为开启；立即上架时**整段省略**（不学文档示例里的 false+时间戳脏数据）。
  // 上架方式显式选了「自动上架」（shelfWay='1'）时同样不排期，防止旧数据残留时间被误提交。
  // 草稿态不排期（与 delivery_option_ids 同口径：AS_DRAFT 不落库）。
  const shelfWayImmediate = asText(form.shelfWay) === '1';
  const scheduleMillis = resolveTiktokScheduledSaleMillis(form.hopeOnSaleDate);
  if (scheduleMillis > 0 && !shelfWayImmediate && asText(saveMode).toUpperCase() !== 'AS_DRAFT') {
    out.scheduled_sale = {
      is_enabled_scheduled_sale: true,
      schedule_sale_time: scheduleMillis,
    };
  }

  // —— 搜索词 / 商品卖点（条数与合计字符双上限，此处为防御性硬截断）——
  const searchTerms = clampTiktokStringList(parseTiktokStringList(form.searchTerms), {
    max: TIKTOK_SEARCH_TERMS_MAX,
    maxTotalChars: TIKTOK_SEARCH_TERMS_MAX_CHARS,
  });
  if (searchTerms.length) out.search_terms = searchTerms;
  const keyFeatures = clampTiktokStringList(parseTiktokStringList(form.keyProductFeatures), {
    max: TIKTOK_KEY_FEATURES_MAX,
    maxTotalChars: TIKTOK_KEY_FEATURES_MAX_CHARS,
  });
  if (keyFeatures.length) out.key_product_features = keyFeatures;

  // —— 限购（purchase_order_quantity_limit）——
  const quantityLimit = resolveTiktokPurchaseOrderLimit(form);
  if (quantityLimit) out.purchase_order_quantity_limit = quantityLimit;

  // —— 幂等键：仅供真实提交，不进 listing_check（文档未收录）——
  const idempotency = asText(idempotencyKey).slice(0, TIKTOK_IDEMPOTENCY_KEY_MAX);
  if (idempotency) out.idempotency_key = idempotency;

  return out;
}

export default buildTiktokTtsCreateProductPayload;
